<template>
  <div class="icon-picker-control">
    <label v-if="label !== ''" class="control-label">{{ label || $t('editor.controls.icon') }}</label>
    <div class="icon-picker-current" @click="togglePicker">
      <img v-if="previewSrc" :src="previewSrc" class="current-icon-preview" alt="" />
      <span class="current-icon-name">{{ selectedName || $t('editor.controls.selectIcon') }}</span>
      <span class="picker-toggle-arrow">&#9662;</span>
    </div>
    <div v-if="isOpen" class="icon-picker-dropdown">
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="text"
        class="icon-search-input"
        :placeholder="$t('editor.controls.searchIcons')"
      />
      <div class="icon-set-tabs">
        <button
          v-for="set in iconSets"
          :key="set.prefix"
          type="button"
          class="icon-set-tab"
          :class="{ 'is-active': !searchQuery && set.prefix === activePrefix }"
          @click="selectSet(set.prefix)"
        >{{ set.label }}</button>
      </div>
      <div v-if="loading" class="no-results">{{ $t('editor.controls.loadingIcons') }}</div>
      <div v-else-if="error" class="no-results">{{ error }}</div>
      <div v-else class="icon-grid">
        <button
          v-for="ref in visibleIcons"
          :key="ref"
          type="button"
          class="icon-grid-item"
          :class="{ 'is-selected': ref === selectedIconRef }"
          :title="iconNameOf(ref)"
          @click="selectIcon(ref)"
        >
          <img v-if="thumbs[ref]" :src="thumbs[ref]" :alt="iconNameOf(ref)" />
        </button>
        <div v-if="visibleIcons.length === 0" class="no-results">{{ $t('editor.controls.noIcons') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ICONIFY_API, DEFAULT_ICON_PREFIX, ICON_SETS, SEARCH_PREFIXES,
  parseIconRef, normalizeIconSvg, iconRefOf, iconJsonUrl, svgFromIconJson, iconNameOf,
} from './iconify';

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 120;
// Names per JSON request: keeps the URL short and a whole collection at a handful of requests.
const THUMB_BATCH = 250;

/** Data URI for an Iconify icon, so a thumbnail never goes to the rate-limited .svg endpoint. */
function svgDataUri(svg) {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export default {
  name: 'IconPickerControl',
  props: {
    elementId: { type: String, required: true },
    // null: the UI word for icon; '' hides the label.
    label: { type: String, default: null },
    currentIcon: { type: String, default: '' }, // stored data-icon value
    currentClasses: { type: String, default: '' }, // slot element's class attr
    iconPrefix: { type: String, default: DEFAULT_ICON_PREFIX },
    // Task 3 (spec 2026-08-15, divider/icon elements): when set, selectIcon
    // emits `select` with { ref, svg } instead of dispatching the V1
    // keditor-update-element window command — lets a v2-element sidebar
    // control (V2ElementSettingsControl.vue) forward the pick through its
    // own command channel. The V1 path (prop absent/false) is unchanged.
    emitOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      // Collection browsing: icon names cached per prefix.
      activePrefix: this.iconPrefix,
      iconsByPrefix: {},
      // Cross-collection search results as full refs ("simple-icons:facebook").
      searchResults: [],
      loading: false,
      error: '',
      iconSets: ICON_SETS,
      // Thumbnails as data URIs per full ref, filled from batched JSON requests.
      thumbs: {},
    };
  },
  computed: {
    selectedRef() {
      return parseIconRef(this.currentIcon);
    },
    selectedIconRef() {
      return this.selectedRef ? iconRefOf(this.selectedRef.prefix, this.selectedRef.name) : '';
    },
    selectedName() {
      // Legacy FontAwesome values ("fa-facebook") have no prefix but are still
      // worth showing, so the field never reads "Select icon" for a set icon.
      if (this.selectedRef) return this.selectedRef.name;
      return this.currentIcon || '';
    },
    previewSrc() {
      if (!this.selectedRef) return '';
      return this.thumbs[this.selectedIconRef] || '';
    },
    visibleIcons() {
      if (this.searchQuery.trim()) return this.searchResults;
      const names = this.iconsByPrefix[this.activePrefix] || [];
      return names.map((name) => iconRefOf(this.activePrefix, name));
    },
  },
  watch: {
    selectedIconRef: {
      immediate: true,
      handler(ref) { if (ref) this.loadThumbs([ref]); },
    },
    visibleIcons(refs) {
      if (this.isOpen) this.loadThumbs(refs);
    },
    searchQuery(q) {
      clearTimeout(this._searchTimer);
      if (!q.trim()) {
        this.searchResults = [];
        this.error = '';
        this.loadCollection(this.activePrefix);
        return;
      }
      this._searchTimer = setTimeout(() => this.runSearch(q.trim()), SEARCH_DEBOUNCE_MS);
    },
  },
  mounted() {
    this._onClickOutside = (e) => {
      if (this.isOpen && this.$el && !this.$el.contains(e.target)) this.isOpen = false;
    };
    document.addEventListener('mousedown', this._onClickOutside);
  },
  beforeDestroy() {
    clearTimeout(this._searchTimer);
    document.removeEventListener('mousedown', this._onClickOutside);
  },
  methods: {
    /**
     * Loads thumbnails over the JSON endpoint, one request per collection and batch.
     * The per-icon .svg endpoint answers 429 once a grid asks for a few hundred icons,
     * which left the grid full of broken images.
     */
    loadThumbs(refs) {
      const missing = {};
      refs.forEach((ref) => {
        if (ref in this.thumbs) return;
        this.$set(this.thumbs, ref, '');
        const idx = ref.indexOf(':');
        if (idx < 1) return;
        const prefix = ref.slice(0, idx);
        (missing[prefix] = missing[prefix] || []).push(ref.slice(idx + 1));
      });
      Object.keys(missing).forEach((prefix) => {
        const names = missing[prefix];
        for (let i = 0; i < names.length; i += THUMB_BATCH) {
          const batch = names.slice(i, i + THUMB_BATCH);
          fetch(`${ICONIFY_API}/${prefix}.json?icons=${batch.join(',')}`)
            .then((r) => {
              if (!r.ok) throw new Error(`Iconify ${r.status}`);
              return r.json();
            })
            .then((json) => {
              batch.forEach((name) => {
                const svg = svgFromIconJson(json, name);
                if (svg) this.$set(this.thumbs, iconRefOf(prefix, name), svgDataUri(svg));
              });
            })
            // A failed batch may be asked again on the next open.
            .catch(() => batch.forEach((name) => this.$delete(this.thumbs, iconRefOf(prefix, name))));
        }
      });
    },
    togglePicker() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        // Open on the collection the current icon came from, when we know it.
        if (this.selectedRef && ICON_SETS.some((s) => s.prefix === this.selectedRef.prefix)) {
          this.activePrefix = this.selectedRef.prefix;
        }
        this.loadCollection(this.activePrefix);
        this.loadThumbs(this.visibleIcons);
        this.$nextTick(() => { if (this.$refs.searchInput) this.$refs.searchInput.focus(); });
      }
    },
    selectSet(prefix) {
      this.searchQuery = '';
      this.searchResults = [];
      this.activePrefix = prefix;
      this.loadCollection(prefix);
    },
    loadCollection(prefix) {
      if (this.iconsByPrefix[prefix]) {
        this.error = '';
        return;
      }
      this.loading = true;
      this.error = '';
      fetch(`${ICONIFY_API}/collection?prefix=${prefix}`)
        .then((r) => r.json())
        .then((data) => {
          let icons = [];
          if (data.uncategorized) {
            icons = data.uncategorized;
          } else if (data.categories) {
            Object.values(data.categories).forEach((cat) => { icons = icons.concat(cat); });
          }
          this.$set(this.iconsByPrefix, prefix, icons.filter((item, i) => icons.indexOf(item) === i));
        })
        .catch(() => { this.error = this.$t('editor.controls.iconsLoadError'); })
        .finally(() => { this.loading = false; });
    },
    runSearch(query) {
      this.loading = true;
      this.error = '';
      const q = encodeURIComponent(query.replace(/\s+/g, '-'));
      fetch(`${ICONIFY_API}/search?query=${q}&prefixes=${SEARCH_PREFIXES}&limit=${SEARCH_LIMIT}`)
        .then((r) => r.json())
        .then((data) => {
          this.searchResults = Array.isArray(data.icons) ? data.icons : [];
        })
        .catch(() => {
          // Offline/blocked search: fall back to filtering the loaded collection.
          const names = this.iconsByPrefix[this.activePrefix] || [];
          const needle = query.toLowerCase().replace(/\s+/g, '-');
          this.searchResults = names
            .filter((name) => name.includes(needle))
            .map((name) => iconRefOf(this.activePrefix, name));
        })
        .finally(() => { this.loading = false; });
    },
    legacyFaClasses() {
      return (this.currentClasses || '')
        .split(/\s+/)
        .filter((c) => /^fa($|-)/.test(c))
        .join(' ');
    },
    selectIcon(ref) {
      this.isOpen = false;
      this.searchQuery = '';
      // JSON endpoint, not the rate-limited .svg one (see iconJsonUrl).
      fetch(iconJsonUrl(ref))
        .then((r) => {
          if (!r.ok) throw new Error(`Iconify ${r.status}`);
          return r.json();
        })
        .then((json) => {
          const svgText = svgFromIconJson(json, iconNameOf(ref));
          if (!svgText) throw new Error(`Iconify: ${ref} not found`);
          const svg = normalizeIconSvg(svgText);
          if (this.emitOnly) {
            this.$emit('select', { ref, svg });
            return;
          }
          window.dispatchEvent(new CustomEvent('keditor-command', {
            detail: {
              type: 'keditor-update-element',
              data: {
                elementId: this.elementId,
                changes: {
                  innerHTML: svg,
                  attr: { 'data-icon': ref },
                  removeClass: this.legacyFaClasses(),
                },
              },
            },
          }));
        })
        .catch(() => { this.error = this.$t('editor.controls.iconInsertError'); });
    },
    iconNameOf,
  },
};
</script>

<style lang="scss" scoped>
.icon-picker-control {
  margin-bottom: 12px;
}

.control-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #555;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.icon-picker-current {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: #bbb;
  }

  .current-icon-preview {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }

  .current-icon-name {
    flex: 1;
    font-size: 13px;
    color: #555;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .picker-toggle-arrow {
    font-size: 10px;
    color: #999;
  }
}

.icon-picker-dropdown {
  margin-top: 4px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.icon-search-input {
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #eee;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #aaa;
  }
}

.icon-set-tabs {
  display: flex;
  gap: 4px;
  padding: 6px 6px 0;
  border-bottom: 1px solid #eee;
}

.icon-set-tab {
  flex: 1;
  padding: 5px 6px;
  border: 1px solid transparent;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  background: none;
  cursor: pointer;
  font-size: 11px;
  color: #777;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: #f5f5f5;
  }

  &.is-active {
    background: #e8eafc;
    border-color: #d5d9f7;
    color: #4f46e5;
    font-weight: 500;
  }
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 2px;
  padding: 6px;
  max-height: 240px;
  overflow-y: auto;
}

.icon-grid-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  border: 1px solid transparent;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  font-size: 16px;
  color: #444;
  padding: 0;
  transition: background-color 0.15s, border-color 0.15s;

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }

  &:hover {
    background: #f0f0f0;
    border-color: #ddd;
  }

  &.is-selected {
    background: #e8eafc;
    border-color: #6366f1;
    color: #6366f1;
  }
}

.no-results {
  grid-column: 1 / -1;
  padding: 16px;
  text-align: center;
  color: #999;
  font-size: 13px;
}
</style>
